<template>
  <modal-fullscreen
    width="600px"
    :visible="visible"
    :destroy-on-close="true"
    data-testid="legal-entity-modal"
    @cancel="closeModal"
  >
    <slot slot="title">
      <div class="flex gap-2 align-items-center">
        <p class="m-0">Legal Entity</p>
        <a-tag class="h-fit" v-if="editingAgency" :color="editingAgency.is_completed ? '#10b981' : '#ef4444'">{{
          editingAgency.is_completed ? 'Complete' : 'Incomplete'
        }}</a-tag>
      </div>
    </slot>
    <a-modal
      :visible="visibleCheckModal"
      :destroy-on-close="true"
      title="Check User"
      data-testid="legal-entity-check-user-modal"
      @cancel="closeCheckModal"
    >
      <a-input v-model="email" placeholder="Enter Email" data-testid="legal-entity-check-user-email-input" />

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeCheckModal">Cancel</a-button>
          <a-button @click="handleCheckSubmit" type="primary" :loading="loadingCheck">Send</a-button>
        </div>
      </template>
    </a-modal>

    <create-associate-modal
      v-if="showCreateAssociate"
      :visible.sync="visibleCreateAssociateModal"
      :is_create_legal_entity="true"
      :user_id="user_id"
      :creation_mode="creatingAssociateType"
      @add="afterCreateAssociate"
    />

    <div class="uncompleted-info grid gap-2">
      <h4 class="col-12 p-0 m-0">Key Criteria for Legal Entity Completion</h4>
      <div class="col-12 p-0 flex flex-column" v-for="item in uncompletedRules" :key="item.key">
        <div
          class="flex align-items-center"
          :style="{
            color: item.rule ? '#10b981' : '#ef4444',
            gap: '8px'
          }"
        >
          {{ item.title }}
          <a-icon :type="item.rule ? 'check-circle' : 'close-circle'" />
        </div>
      </div>
    </div>

    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-4"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <div class="flex flex-column gap-2">
        <h3>Info</h3>
        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Name"
          prop="name"
          extra="Business name used to identify this Legal Entity throughout ePeople. Up to 64 characters."
        >
          <a-input :disabled="isDisabledUpdate" v-model="form.name" placeholder="Enter Name" />
        </a-form-model-item>
        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Code"
          prop="code"
          extra="Internal reference code for this Legal Entity. Use a short value of up to 64 characters."
        >
          <a-input :disabled="isDisabledUpdate" v-model="form.code" placeholder="Enter Code" />
        </a-form-model-item>

        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Type"
          prop="type"
          :extra="legalEntityTypeExtra"
        >
          <a-select
            :disabled="form.staffing_status === 'linked' || isDisabledUpdate"
            v-model="form.type"
            placeholder="Legal Entity Type"
          >
            <a-select-option v-for="(value, key) in AGENCY_TYPES" :key="key" :value="key">
              {{ value }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item
          :colon="false"
          label-align="left"
          :label="form.type === 'agency' || form.type === 'marketplace' ? 'Tax ID (EIN)' : 'Tax ID (SSN)'"
          prop="tax_id"
          :extra="taxIdExtra"
        >
          <a-input
            :key="form.type"
            :disabled="form.staffing_status === 'linked' || isDisabledUpdate"
            v-model="form.tax_id"
            :masked="true"
            raw
            v-mask="form.type === 'agency' || form.type === 'marketplace' ? '##-#######' : '###-##-####'"
            :placeholder="form.type === 'agency' || form.type === 'marketplace' ? 'Tax ID (EIN)' : 'Tax ID (SSN)'"
            ><a-icon v-show="loadingProject" slot="suffix" type="loading"
          /></a-input>
        </a-form-model-item>

        <div class="grid py-2" v-if="project">
          <div class="col py-0">
            <p class="text-lg font-bold m-0">Connect Your Company with {{ project.name }}</p>
            <p>
              Connecting will allow you to pay for Invoices from the Staffing Company and receive Documents about their
              staff.
            </p>
          </div>
          <div class="col-fixed">
            <a-switch
              :disabled="isDisabledUpdate || loadingLink"
              :loading="loadingLink"
              :checked="isLinked"
              @change="handleLinkChange"
            />
          </div>
        </div>

        <template v-if="showAssociateSearch">
          <a-form-model-item
            v-show="form.type === 'agency' || form.type === 'marketplace'"
            :colon="false"
            label-align="left"
            label="Staff"
            prop="associate_ids"
            extra="Associates who work under this Legal Entity. Available for Agency and Marketplace entities."
          >
            <search-associates :multiply="true" :value.sync="form.associate_ids" :is-associate-id="true">
              <template v-if="showCreateAssociate" slot="dropdown">
                <div
                  style="padding: 4px 8px; cursor: pointer"
                  @mousedown="e => e.preventDefault()"
                  @click="openCheckModal('worker')"
                >
                  <a-icon type="plus" /> Add Associate
                </div>
                <a-divider style="margin: 0" /></template
            ></search-associates>
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            :label="form.type === 'independent_contractor' ? 'Independent Contractor' : 'Management'"
            prop="admin_ids"
            :extra="
              form.type === 'independent_contractor'
                ? 'Associate who owns or manages this Independent Contractor Legal Entity.'
                : 'Associates who manage this Legal Entity.'
            "
          >
            <search-associates
              :multiply="true"
              :value.sync="form.admin_ids"
              :is-associate-id="true"
              :max-count="form.type === 'agency' || form.type === 'marketplace' ? null : 1"
            >
              <template v-if="showCreateAssociate" slot="dropdown">
                <div
                  style="padding: 4px 8px; cursor: pointer"
                  @mousedown="e => e.preventDefault()"
                  @click="openCheckModal('legal_admin')"
                >
                  <a-icon type="plus" /> Add Associate
                </div>
                <a-divider style="margin: 0" /></template
            ></search-associates>
          </a-form-model-item>

          <a-alert
            v-if="
              form.type === 'agency' || form.type === 'marketplace'
                ? !form.associate_ids.length && !form.admin_ids.length
                : !form.associate_ids.length
            "
            message="This Legal Entity does NOT CONTAIN any associates or managers working for this company. It seems unused or not fully configured! Please either fill it with members or delete it."
            type="error"
            show-icon
          />
        </template>
      </div>
      <div class="flex flex-column gap-2">
        <h3>Address</h3>
        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Address Line 1"
          prop="address.address_line1"
          extra="Street number and street name of the Legal Entity’s business address."
        >
          <a-input :disabled="isDisabledUpdate" v-model="form.address.address_line1" placeholder="Enter Address" />
        </a-form-model-item>
        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Address Line 2"
          prop="address.address_line2"
          extra="Optional suite, unit, floor, or other address details."
        >
          <a-input :disabled="isDisabledUpdate" v-model="form.address.address_line2" placeholder="Enter Address" />
        </a-form-model-item>

        <a-form-model-item
          :colon="false"
          label-align="left"
          label="City"
          prop="address.city"
          extra="City of the Legal Entity’s business address."
        >
          <a-input :disabled="isDisabledUpdate" v-model="form.address.city" placeholder="Enter City" />
        </a-form-model-item>

        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Country"
          prop="address.country"
          extra="Country of the Legal Entity’s business address."
        >
          <a-select :disabled="isDisabledUpdate" v-model="form.address.country" placeholder="Enter Country">
            <a-select-option v-for="country in countriesList" :key="country.code" :value="country.code">
              {{ country.name }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item
          v-if="form.address.country === 'US'"
          :colon="false"
          label-align="left"
          label="State"
          prop="address.state"
          extra="US state of the Legal Entity’s business address."
        >
          <a-select :disabled="isDisabledUpdate" v-model="form.address.state" placeholder="Enter State">
            <a-select-option v-for="state in statesList" :key="state.code" :value="state.code">
              {{ state.name }}
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item
          :colon="false"
          label-align="left"
          label="Zipcode"
          prop="address.zipcode"
          extra="ZIP or postal code of the Legal Entity’s business address."
        >
          <a-input :disabled="isDisabledUpdate" v-model="form.address.zipcode" placeholder="Enter Zipcode" />
        </a-form-model-item>
      </div>

      <div class="flex flex-column gap-2">
        <h3 class="m-0">Conformation Documents</h3>

        <a-alert type="info" show-icon
          ><p slot="message">You must have the following documents to work with Legal Entity</p></a-alert
        >

        <div class="flex flex-column gap-2">
          <template v-if="form.type === 'agency' || form.type === 'marketplace'"
            ><h4 class="m-0">Tax ID Letter from the IRS</h4>

            <legal-entity-attachments
              :with-title="false"
              :files="form.tax_id_files"
              @save="ids => saveAttachmentsIds(ids, 'tax_id_files')" />

            <h4 class="m-0">Articles of Organization</h4>

            <legal-entity-attachments
              :with-title="false"
              :files="form.article_files"
              @save="ids => saveAttachmentsIds(ids, 'article_files')"
          /></template>

          <h4 class="m-0">Temporary Service Agreement (Contract)</h4>

          <legal-entity-attachments
            :with-title="false"
            :files="form.contract_files"
            @save="ids => saveAttachmentsIds(ids, 'contract_files')"
          />

          <h4 class="m-0">W9 Form</h4>

          <legal-entity-attachments
            :with-title="false"
            :files="form.w9_files"
            @save="ids => saveAttachmentsIds(ids, 'w9_files')"
          />

          <h4 class="m-0">Extra Files</h4>

          <legal-entity-attachments
            :with-title="false"
            :files="form.files"
            @save="ids => saveAttachmentsIds(ids, 'files')"
          />
        </div>
      </div>

      <div class="grid" v-show="form.type === 'agency' || form.type === 'marketplace'">
        <div class="col py-0">
          <p class="text-lg font-bold m-0">Onboarding has been completed and confirmed by the Legal Entity</p>
          <p>Legal Entity handles Employee Onboarding on its own and provides these documents.</p>
        </div>
        <div class="col-fixed">
          <a-switch :checked="!form.onboarding_available" :disabled="true" />
        </div>
      </div>

      <div class="grid">
        <div class="col py-0">
          <p class="text-lg font-bold m-0">Toggle Approvals Off</p>
          <p>This option lets you disable or enable approval requirements for every person in this Legal Entity.</p>
        </div>
        <div class="col-fixed">
          <a-switch v-model="form.disable_auth" />
        </div>
      </div>

      <div class="grid">
        <div class="col py-0">
          <p class="text-lg font-bold m-0">Ignore Blocking</p>
          <p>
            Workers from this Legal Entity cannot be blocked for any reason. Be careful when enabling this flag, as it
            may lead to non-compliant data.
          </p>
        </div>
        <div class="col-fixed">
          <a-switch v-model="form.disable_block" />
        </div>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Cancel</a-button>
        <a-button @click="handleSubmit" type="primary" :loading="loading">Save</a-button>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import {
  Button,
  Select,
  Input,
  Switch,
  notification,
  FormModel,
  Alert,
  Modal,
  Icon,
  Tag,
  Divider
} from 'ant-design-vue'
import ConstData from '@/helpers/const-data'
import { createNamespacedHelpers } from 'vuex'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { debounce } from '@/helpers/debounce'
import { deepCopy } from '@/utils/deep-copy'
import SearchAssociates from '@/components/search-associates.vue'
import { AGENCY_TYPES } from '../../../store/modules/legal-entity/constants'
const CreateAssociateModal = () => import('../../profile/components/profile-create-associate-modal.vue')

const { mapActions: legalActions } = createNamespacedHelpers('legalEntity')
const { mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapActions: projectActions } = createNamespacedHelpers('project')

const initialForm = {
  name: undefined,
  code: undefined,
  type: 'agency',
  tax_id: undefined,
  associate_ids: [],
  admin_ids: [],
  files: [],
  address: {
    address_line1: undefined,
    address_line2: undefined,
    city: undefined,
    state: 'CA',
    country: 'US',
    zipcode: undefined
  },
  disable_auth: false,
  disable_block: false
}

const initialAttachments = {
  article_files: [],
  contract_files: [],
  files: [],
  tax_id_files: [],
  w9_files: []
}

const initialRules = {
  name: [
    {
      required: true,
      message: 'Name can not be empty',
      trigger: 'change'
    }
  ],
  type: [
    {
      required: true,
      message: 'Type can not be empty',
      trigger: 'change'
    }
  ],
  tax_id: [
    {
      len: 9,
      message: 'Tax ID must be contain 9 digit',
      trigger: 'change',
      transform: value => (value ? value.replace(/\D/g, '') : value)
    }
  ],
  'address.city': [
    {
      required: true,
      message: 'City can not be empty',
      trigger: 'change'
    }
  ],
  'address.address_line1': [
    {
      required: true,
      message: 'Address Line 1 can not be empty',
      trigger: 'change'
    }
  ],
  'address.country': [
    {
      required: true,
      message: 'Country can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  name: 'legal-entity-modal',
  components: {
    'a-button': Button,
    'a-input': Input,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-switch': Switch,
    'a-form-model-item': FormModel.Item,
    'a-form-model': FormModel,
    'a-alert': Alert,
    'a-icon': Icon,
    'a-tag': Tag,
    'a-divider': Divider,
    'search-associates': SearchAssociates,
    'legal-entity-attachments': ShiftFormAttachments,
    CreateAssociateModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    editingAgency: {
      type: Object,
      default: null
    },
    initialValues: {
      type: Object,
      default: null
    },
    showCreateAssociate: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      loadingCheck: false,
      loadingLink: false,
      loadingProject: false,

      visibleCheckModal: false,
      visibleCreateAssociateModal: false,

      form: { ...initialForm },
      rules: {
        ...initialRules
      },

      countriesList: ConstData.countries,
      statesList: ConstData.usStates,
      AGENCY_TYPES,

      email: '',
      user_id: undefined,
      creatingAssociateType: undefined,

      isInitializingForm: false,
      isLinked: false,
      project: undefined,

      attachmentsIds: {
        ...initialAttachments
      }
    }
  },
  computed: {
    legalEntityTypeExtra() {
      const description =
        'Independent Contractor—an individual-owned entity; Agency—a business managing its own staff; Marketplace—an entity representing workers sourced through Marketplace.'

      return this.form.staffing_status === 'linked'
        ? `${description} Disconnect the Legal Entity before changing its type.`
        : description
    },

    taxIdExtra() {
      const description =
        this.form.type === 'agency' || this.form.type === 'marketplace'
          ? 'Enter the nine-digit EIN in XX-XXXXXXX format.'
          : 'Enter the nine-digit SSN in XXX-XX-XXXX format.'

      return this.form.staffing_status === 'linked'
        ? `${description} Disconnect the Legal Entity before changing its Tax ID.`
        : description
    },

    showAssociateSearch() {
      return (
        this.showCreateAssociate &&
        (this.form.type === 'independent_contractor' || this.form.type === 'agency' || this.form.type === 'marketplace')
      )
    },

    isDisabledUpdate() {
      return this.editingAgency?.parent_entity && !this.isProjectHost
    },

    isProjectHost() {
      return this.$store.state.applicationState.currentProject.sequences_host
    },

    uncompletedRules() {
      const rules = [
        {
          title: this.form.tax_id ? 'Tax ID is Filled In' : 'Tax ID is not Filled In',
          rule: !!this.form.tax_id,
          key: 'tax_id'
        },
        {
          title: this.editingAgency ? (this.form.is_verified ? 'Verified' : 'Unverified') : 'Verified',
          rule: this.editingAgency ? this.form.is_verified : true,
          key: 'verified'
        },
        {
          title: `Uploaded ${
            this.form.type === 'agency' || this.form.type === 'marketplace' ? '4' : '2'
          } Required Documents`,
          rule:
            this.form.type === 'agency' || this.form.type === 'marketplace'
              ? !!this.attachmentsIds.article_files.length &&
                !!this.attachmentsIds.w9_files.length &&
                !!this.attachmentsIds.contract_files.length &&
                !!this.attachmentsIds.tax_id_files.length
              : !!this.attachmentsIds.contract_files.length && !!this.attachmentsIds.w9_files.length,
          key: 'attachments'
        },
        {
          title:
            !!this.form.associate_ids.length || !!this.form.admin_ids.length
              ? 'Members Assigned'
              : 'Members not Assigned',
          rule: !!this.form.associate_ids.length || !!this.form.admin_ids.length,
          key: 'members'
        }
      ]

      return rules
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.isInitializingForm = true

          if (this.editingAgency) {
            this.form = deepCopy(this.editingAgency)
            this.form.tax_id = this.form.tax_id ? this.form.tax_id.replace(/\D/g, '') : undefined
            ;['w9_files', 'tax_id_files', 'files', 'contract_files', 'article_files'].forEach(item => {
              this.attachmentsIds[item] = this.form[item].map(item => ({
                id: item.id
              }))
            })

            this.isLinked = this.editingAgency.staffing_status === 'linked'
            this.form.admin_ids =
              this.form.type === 'agency' || this.form.type === 'marketplace'
                ? deepCopy(this.form.admin_ids)
                : this.form.associate_ids.length
                  ? deepCopy(this.form.associate_ids)
                  : deepCopy(this.form.admin_ids)

            if (this.form.tax_id) {
              this.rules = {
                ...initialRules,
                tax_id: [
                  {
                    required: true,
                    message: 'Tax ID can not be empty',
                    trigger: 'change'
                  },
                  {
                    len: 9,
                    message: 'Tax ID must be contain 9 digit',
                    trigger: 'change',
                    transform: value => (value ? value.replace(/\D/g, '') : value)
                  }
                ]
              }
            }
          } else {
            this.form = deepCopy(initialForm)
            this.rules = deepCopy(initialRules)
            this.isLinked = false
            this.project = undefined
          }

          if (this.initialValues) {
            this.form.type = this.initialValues.type
            this.form.address = deepCopy(this.initialValues.address)
            this.form.name = this.initialValues.name
            this.form.tax_id = this.initialValues.tax_id
            this.form.admin_ids = deepCopy(this.initialValues.admin_ids ?? [])
            this.form.associate_ids =
              this.form.type === 'independent_contractor'
                ? deepCopy(this.form.admin_ids)
                : deepCopy(this.initialValues.associate_ids ?? [])
          }

          this.$nextTick(() => {
            this.isInitializingForm = false
          })
        }
      },
      immediate: true
    },

    'form.type': {
      handler(newVal, oldVal) {
        if (this.isInitializingForm || newVal === oldVal) return

        this.normalizeMembersAfterTypeChange(newVal, oldVal)
      },
      immediate: false
    },

    'form.address.country': {
      handler(newVal) {
        if (newVal !== 'US') this.form.address.state = undefined
      }
    },
    'form.tax_id': {
      handler: debounce(function (newVal) {
        this.fetchProjectByTaxId(newVal)
      }, 1000)
    },
    'form.associate_ids': {
      handler(newVal) {
        this.handleAssociateIdsChange(newVal)
      },
      deep: true
    },
    'form.admin_ids': {
      handler(newVal) {
        this.handleAdminsIdsChange(newVal)

        if (this.form.type === 'independent_contractor') {
          this.form.associate_ids = [...newVal]
        }
      },
      deep: true
    }
  },

  methods: {
    ...legalActions(['createLegalEntity', 'getLegalEntity', 'updateLegalEntity', 'postLinkProject']),
    ...profileActions(['checkExistingUser']),
    ...projectActions(['getProjectByTaxId']),

    saveAttachmentsIds(ids, type) {
      this.attachmentsIds[type] = [...ids]
    },

    async fetchProjectByTaxId(newVal) {
      if (!newVal || newVal.replace(/\D/g, '').length !== 9) {
        this.project = undefined
        return
      }

      this.loadingProject = true

      try {
        const result = await this.getProjectByTaxId({
          tax_id: newVal.replace(/\D/g, ''),
          exclude_id: this.editingAgency ? this.editingAgency.project_id : undefined
        })

        this.project = result
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingProject = false
      }
    },

    handleAssociateIdsChange(newVal) {
      if (this.showAssociateSearch && this.$refs.form) {
        this.$nextTick(() => {
          this.$refs.form.validateField('associate_ids')
        })
      }

      if (this.form.type === 'agency' || this.form.type === 'marketplace') {
        const filteredIds = newVal.filter(item => !this.form.admin_ids.includes(item))

        const addedAssociate = this.form.admin_ids.find(item => newVal.includes(item))

        if (JSON.stringify(filteredIds) !== JSON.stringify(newVal)) {
          this.showNotification('info', 'Info', 'Associate has been updated from Manager to Staff.')

          this.form.admin_ids = this.form.admin_ids.filter(item => item !== addedAssociate)
        }
      }
    },

    handleAdminsIdsChange(newVal) {
      if (this.showAssociateSearch && this.$refs.form) {
        this.$nextTick(() => {
          this.$refs.form.validateField('admin_ids')
        })
      }

      if (this.form.type === 'agency' || this.form.type === 'marketplace') {
        const filteredIds = newVal.filter(item => !this.form.associate_ids.includes(item))

        const addedAdmin = this.form.associate_ids.find(item => newVal.includes(item))

        if (JSON.stringify(filteredIds) !== JSON.stringify(newVal)) {
          this.showNotification('info', 'Info', 'Associate has been updated from Staff to Manager.')

          this.form.associate_ids = this.form.associate_ids.filter(item => item !== addedAdmin)
        }
      }
    },

    normalizeMembersAfterTypeChange(newType, oldType) {
      if (newType === 'independent_contractor') {
        this.form.admin_ids = []
        this.form.associate_ids = []
      }
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closeModal() {
      this.attachmentsIds = deepCopy(initialAttachments)
      this.form = deepCopy(initialForm)
      this.rules = deepCopy(initialRules)
      this.isInitializingForm = false
      this.isLinked = false
      this.project = undefined
      this.$emit('update:visible', false)
      this.$emit('update:editingAgency', null)
    },

    openCheckModal(type) {
      this.creatingAssociateType = type
      this.visibleCheckModal = true
    },

    closeCheckModal() {
      this.visibleCheckModal = false
    },

    openCreateAssociateModal() {
      this.visibleCreateAssociateModal = true
    },

    afterCreateAssociate(associate) {
      if (this.creatingAssociateType === 'worker') {
        this.form.associate_ids = [...this.form.associate_ids, associate.id]
      } else {
        this.form.admin_ids = [...this.form.admin_ids, associate.id]
      }

      this.$refs[this.creatingAssociateType].fetchAssociates()

      this.creatingAssociateType = undefined
    },

    async handleCheckSubmit() {
      try {
        this.loadingCheck = true

        const data = await this.checkExistingUser({
          email: this.email.toLowerCase()
        })

        if (data.user_profile) {
          this.user_id = data.user_profile.user_id
        } else {
          this.user_id = undefined
        }

        this.openCreateAssociateModal()
        this.closeCheckModal()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingCheck = false
      }
    },

    validateForm() {
      return new Promise(resolve => {
        this.$refs.form.validate(valid => {
          resolve(valid)
        })
      })
    },

    getLegalEntityPayload() {
      const files =
        this.form.type === 'agency' || this.form.type === 'marketplace'
          ? { ...this.attachmentsIds }
          : {
              files: this.attachmentsIds.files,
              w9_files: this.attachmentsIds.w9_files,
              contract_files: this.attachmentsIds.contract_files,
              tax_id_files: [],
              article_files: []
            }

      return {
        legal_entity: {
          ...this.form,
          tax_id: this.form.tax_id ? this.form.tax_id.replace(/\D/g, '') : undefined,
          admin_ids:
            this.form.type === 'agency' || this.form.type === 'marketplace'
              ? this.form.admin_ids
              : this.form.associate_ids,
          ...files
        }
      }
    },

    async saveLegalEntity() {
      const payload = this.getLegalEntityPayload()

      return this.editingAgency ? await this.updateLegalEntity(payload) : await this.createLegalEntity(payload)
    },

    applyLegalEntityData(legalEntity) {
      this.form = deepCopy(legalEntity)
      this.form.tax_id = this.form.tax_id ? this.form.tax_id.replace(/\D/g, '') : undefined
      ;[('w9_files', 'tax_id_files', 'files', 'contract_files', 'article_files')].forEach(item => {
        this.attachmentsIds[item] = this.form[item].map(item => ({
          id: item.id
        }))
      })

      this.isLinked = legalEntity.staffing_status === 'linked'
      this.form.admin_ids =
        this.form.type === 'agency' || this.form.type === 'marketplace'
          ? deepCopy(this.form.admin_ids)
          : this.form.associate_ids.length
            ? deepCopy(this.form.associate_ids)
            : deepCopy(this.form.admin_ids)
    },

    async refreshLegalEntity(legalEntityId) {
      const legalEntity = await this.getLegalEntity({
        legal_entity_id: legalEntityId
      })

      this.applyLegalEntityData(legalEntity)
      this.$emit('update:editingAgency', legalEntity)
      this.$emit('add', legalEntity)

      return legalEntity
    },

    async handleLinkChange(checked) {
      const previousIsLinked = !checked

      this.isLinked = checked
      if (!this.editingAgency) return

      const legalEntityId = this.form.id || this.editingAgency.id
      const staffingStatus = this.form.staffing_status || this.editingAgency.staffing_status
      try {
        this.loadingLink = true

        const valid = await this.validateForm()

        if (!valid) {
          this.isLinked = previousIsLinked
          this.showNotification('error', 'Error', 'Please fill in required fields before changing the connection.')
          return
        }

        if (this.shouldSkipProjectLink(staffingStatus, checked)) {
          this.isLinked = previousIsLinked
          return
        }

        const isConfirmed = await this.confirmProjectLink(staffingStatus, checked)

        if (!isConfirmed) {
          this.isLinked = previousIsLinked
          return
        }

        const data = await this.saveLegalEntity()

        await this.updateProjectLink(data.id || legalEntityId, checked)
        await this.refreshLegalEntity(data.id || legalEntityId)
      } catch (error) {
        this.isLinked = previousIsLinked
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loadingLink = false
      }
    },

    async handleSubmit() {
      const valid = await this.validateForm()

      if (!valid) return

      try {
        this.loading = true
        const data = await this.saveLegalEntity()

        this.$emit('add', data)

        const isLinkUpdated = await this.handleProjectLink(data.staffing_status, data.id)

        this.showNotification(
          'success',
          'Success',
          `Legal Entity has been ${this.editingAgency ? 'edited' : 'created'} successfully`
        )

        this.$emit('add', {
          ...data,
          staffing_status:
            data.staffing_status === 'unavailable'
              ? 'unavailable'
              : isLinkUpdated
                ? this.isLinked
                  ? 'linked'
                  : 'unlinked'
                : data.staffing_status
        })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.closeModal()
        this.loading = false
      }
    },

    shouldSkipProjectLink(staffing_status, isLinked = this.isLinked) {
      return (
        (staffing_status === 'unavailable' && !this.project) ||
        (staffing_status === 'linked' && isLinked) ||
        (staffing_status === 'unlinked' && !isLinked)
      )
    },

    async confirmProjectLink(staffing_status, isLinked = this.isLinked) {
      if (this.shouldSkipProjectLink(staffing_status, isLinked)) return true
      if (!this.project) return false

      return await new Promise(resolve => {
        Modal.confirm({
          title: isLinked ? `Connect to ${this.project.name}?` : `Disconnect from ${this.project.name}?`,
          content: isLinked
            ? "By connecting, you'll allow the Staffing Company to send you Invoices. You'll also receive Documents about their Staff. Profiles data between the companies will be SYNCHRONIZED. Are you sure you want to connect? "
            : 'By disconnecting, the Staffing Company will no longer be able to send you Invoices, and you will stop receiving Staff Documents. Profiles between the companies will become UNSYNCHRONIZED.',
          okText: 'Confirm',
          onOk: () => {
            resolve(true)
          },
          onCancel: () => {
            resolve(false)
          }
        })
      })
    },

    async updateProjectLink(legal_entity_id, isLinked = this.isLinked) {
      await this.postLinkProject({
        legal_entity_id,
        type: isLinked ? 'link_to_staff' : 'unlink_from_staff'
      })

      if (isLinked) {
        this.showNotification('success', 'Success', 'Please wait, the connection process takes some time!')
      }
    },

    async handleProjectLink(staffing_status, legal_entity_id) {
      const isLinked = this.isLinked

      if (this.shouldSkipProjectLink(staffing_status, isLinked)) return true

      const isConfirmed = await this.confirmProjectLink(staffing_status, isLinked)

      if (!isConfirmed) return false

      await this.updateProjectLink(legal_entity_id, isLinked)

      return true
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.uncompleted-info {
  position: absolute;
  right: -290px;
  background-color: var(--bg-component);
  width: 270px;
  height: fit-content;
  top: 6px;
  border-radius: 12px;
  padding: 16px 20px;
}

::v-deep .ant-modal-body {
  overflow-y: auto;
  max-height: 600px;
}

.modal-fullscreen-wrap--expanded .uncompleted-info {
  position: static;
  padding: 0;
  width: 100%;
  margin: 0;
  padding-bottom: 24px;
}

::v-deep .modal-fullscreen-wrap--expanded .ant-modal-body {
  max-height: none;
}

@media screen and (max-width: 1200px) {
  .uncompleted-info {
    position: absolute;
    right: -200px;
    background-color: var(--bg-component);
    width: 200px;
    height: fit-content;
    top: 6px;
    border-radius: 12px;
    padding: 12px 16px;
  }
}

@media screen and (max-width: 1024px) {
  .uncompleted-info {
    position: static;
    padding: 0;
    width: 100%;
    margin: 0;
    padding-bottom: 24px;
  }

  .uncompleted-info h4 {
    font-size: 18px;
    margin-bottom: 14px !important;
  }
}
</style>
